import type { BoardEntry } from './types';
import { generateKatex, validateKatex } from '@/lib/formulaGenerator';
import { generateNumberLinePayload } from '@/lib/numberLineGenerator';
import { generateCoordinatePlanePayload } from '@/lib/coordinatePlaneGenerator';
import { generateGeometryPayload } from '@/lib/geometryGenerator';
import { mergeDiagramPayload } from './diagramMerge';

type StarState = 'pending' | 'success' | 'fail';

/**
 * Handler function signature for transcription-based tool execution.
 * Tools are invoked by parsing [TOOL_START]...[TOOL_END] markers from
 * Gemini's speech transcription (not via Gemini function calling).
 */
export interface TranscriptionToolHandler {
  name: string;
  handler: (args: Record<string, string | number>) => Promise<{ response: Record<string, unknown> }> | { response: Record<string, unknown> };
}

/**
 * Creates V2 whiteboard tool handlers for transcription-based execution.
 * Tools: write_board, clear_board, award_star.
 *
 * write_board rendering is determined by `type` and `render` parameters:
 * - type="heading": content is displayed as plain text (section labels)
 * - type="step" | "concept" | "question": content is a natural-language
 *   description. By default render="katex" (NL → KaTeX).
 * - render="number_line": content is natural-language number-line description
 *   converted into structured payload for diagram rendering.
 * - render="coordinate_plane": content is natural-language coordinate-plane
 *   description converted into structured payload for diagram rendering.
 * - render="geometry": content is natural-language geometry description
 *   converted into structured payload for diagram rendering.
 */
// ---------------------------------------------------------------------------
// Dedup helpers – catch near-duplicate write_board calls
// ---------------------------------------------------------------------------

/** Remove all whitespace, normalize punctuation, and lowercase for fuzzy comparison */
function normalizeForDedup(s: string): string {
  return s.toLowerCase().replace(/\s+/g, '').replace(/;/g, ',');
}

/** Strip common label prefixes (e.g. "Group like terms: 3x-x" → "3x-x") */
function extractMathPortion(s: string): string {
  return normalizeForDedup(s.replace(/^[a-z\s]+:\s*/i, ''));
}

/** Check if newContent is a near-duplicate of any existing board entry */
function isDuplicateContent(newContent: string, boardContents: string[]): boolean {
  const normalizedNew = normalizeForDedup(newContent);
  const mathNew = extractMathPortion(newContent);

  return boardContents.some((existing) => {
    const normalizedExisting = normalizeForDedup(existing);

    // Case 1: Identical after whitespace removal
    if (normalizedNew === normalizedExisting) return true;

    // Case 2: Same math expression, different label prefix
    const mathExisting = extractMathPortion(existing);
    if (mathNew.length > 0 && mathNew === mathExisting) return true;

    // Case 3: One extends the other by "= ?" (e.g. "expr" vs "expr = ?")
    const [shorter, longer] =
      normalizedNew.length <= normalizedExisting.length
        ? [normalizedNew, normalizedExisting]
        : [normalizedExisting, normalizedNew];
    if (longer.startsWith(shorter)) {
      const tail = longer.slice(shorter.length);
      if (tail === '=?' || tail === '=') return true;
    }

    return false;
  });
}

export function createV2ToolDefinitions(
  appendEntry: (entry: BoardEntry) => void,
  clearEntries: () => void,
  clearHeader?: () => void,
  updateStarState?: (index: number, state: StarState) => void,
  setHighlightIndex?: (index: number | null) => void,
  diagramContext?: string,
  updateEntry?: (id: string, next: BoardEntry) => void,
): { tools: TranscriptionToolHandler[]; getBoardContents: () => string[] } {
  let entryCounter = 0;
  let totalToolCalls = 0;
  // Mirror of current board contents, returned to model for context
  const boardContents: string[] = [];
  // Mirror of current board entries for tool lookups (diagrams)
  const entriesMirror: BoardEntry[] = [];

  // Track star states internally to prevent duplicate awards
  const starStates: StarState[] = ['pending', 'pending', 'pending'];

  const tools: TranscriptionToolHandler[] = [
    {
      name: 'write_board',
      handler: async (args) => {
        totalToolCalls += 1;
        const { content, type, render } = args as { content?: string; type?: string; render?: string };
        if (!content?.trim()) {
          return { response: { result: 'ignored', reason: 'empty_content', board: [...boardContents] } };
        }

        const originalContent = content.trim();
        const renderMode: BoardEntry['render'] =
          render === 'number_line'
            ? 'number_line'
            : render === 'coordinate_plane'
              ? 'coordinate_plane'
              : render === 'geometry'
                ? 'geometry'
              : 'katex';

        // Deduplicate against ALL existing board entries (fuzzy: ignores whitespace, label prefixes, trailing "= ?")
        if (isDuplicateContent(originalContent, boardContents)) {
          return { response: { result: 'ignored', reason: 'duplicate', board: [...boardContents] } };
        }

        const entry: BoardEntry = {
          id: `entry-${entryCounter + 1}`,
          content: originalContent,
          type: (type as BoardEntry['type']) || 'step',
          render: renderMode,
          timestamp: Date.now(),
        };

        if (renderMode === 'number_line') {
          try {
            entry.payload = await generateNumberLinePayload(originalContent, diagramContext);
          } catch (error) {
            console.warn('[write_board] Number-line generation failed:', error);
            return {
              response: {
                result: 'error',
                reason: error instanceof Error ? error.message : 'number_line_generation_failed',
                board: [...boardContents],
              },
            };
          }
        } else if (renderMode === 'coordinate_plane') {
          try {
            entry.payload = await generateCoordinatePlanePayload(originalContent, diagramContext);
          } catch (error) {
            console.warn('[write_board] Coordinate-plane generation failed:', error);
            return {
              response: {
                result: 'error',
                reason: error instanceof Error ? error.message : 'coordinate_plane_generation_failed',
                board: [...boardContents],
              },
            };
          }
        } else if (renderMode === 'geometry') {
          try {
            entry.payload = await generateGeometryPayload(originalContent, diagramContext);
          } catch (error) {
            console.warn('[write_board] Geometry generation failed:', error);
            return {
              response: {
                result: 'error',
                reason: error instanceof Error ? error.message : 'geometry_generation_failed',
                board: [...boardContents],
              },
            };
          }
        } else {
          let finalContent: string;

          // heading/subheading = plain text; everything else = formula (NL → KaTeX)
          if (type === 'heading' || type === 'subheading') {
            // Strip "Heading: " / "Subheading: " prefix that Gemini sometimes prepends
            finalContent = originalContent.replace(/^(Heading|Subheading):\s*/i, '');
          } else {
            try {
              const katexStr = await generateKatex(originalContent);

              if (validateKatex(katexStr)) {
                finalContent = katexStr;
              } else {
                // Retry once
                console.warn('[write_board] KaTeX validation failed, retrying…');
                const retry = await generateKatex(originalContent);
                if (validateKatex(retry)) {
                  finalContent = retry;
                } else {
                  console.warn('[write_board] Retry also failed, falling back to text');
                  finalContent = originalContent;
                }
              }
            } catch (error) {
              console.warn('[write_board] Formula generation failed, falling back to text:', error);
              finalContent = originalContent;
            }
          }

          entry.renderedContent = finalContent;
        }

        entryCounter += 1;
        boardContents.push(originalContent);
        entry.id = `entry-${entryCounter}`;
        appendEntry(entry);
        entriesMirror.push(entry);
        return {
          response: {
            result: 'ok',
            render: renderMode,
            ...(entry.renderedContent ? { katex: entry.renderedContent } : {}),
            ...(entry.payload ? { payload: entry.payload } : {}),
            board: [...boardContents],
          },
        };
      },
    },
    {
      name: 'clear_board',
      handler: async (args) => {
        totalToolCalls += 1;
        const { scope } = args as { scope?: string };
        // scope="problem plus notes" → clear entries AND the problem header
        // scope="notes only" (default) → clear entries only, keep the problem header
        const clearAll = scope === 'problem plus notes';

        // Ignore clear_board if it's the very first tool call — the problem
        // is already displayed on the board and clearing would erase it.
        if (totalToolCalls === 1) {
          console.warn('[clear_board] Ignored: first tool call cannot be clear_board');
          return { response: { result: 'ignored', reason: 'first_call', board: [...boardContents] } };
        }
        entryCounter = 0;
        boardContents.length = 0;
        entriesMirror.length = 0;
        clearEntries();
        if (clearAll) {
          clearHeader?.();
        }
        return { response: { result: 'ok', board: [] } };
      },
    },
    {
      name: 'award_star',
      handler: async (args) => {
        totalToolCalls += 1;
        const { checkpoint } = args as { checkpoint?: number };

        if (!checkpoint || checkpoint < 1 || checkpoint > 3) {
          return { response: { error: 'Invalid checkpoint (must be 1, 2, or 3)' } };
        }

        // Awarding checkpoint N should also grant any earlier pending checkpoints.
        // This prevents ending in states like "only the 3rd star is lit".
        const maxIndex = checkpoint - 1;
        const newlyAwarded: number[] = [];
        for (let idx = 0; idx <= maxIndex; idx += 1) {
          if (starStates[idx] !== 'pending') continue;
          starStates[idx] = 'success';
          updateStarState?.(idx, 'success');
          newlyAwarded.push(idx + 1);
        }
        setHighlightIndex?.(null);

        if (newlyAwarded.length === 0) {
          return { response: { result: 'already_awarded', checkpoint } };
        }

        console.log('[award_star]', { checkpoint, newlyAwarded });

        return { response: { result: 'ok', checkpoint, newlyAwarded } };
      },
    },
    {
      name: 'update_last_diagram',
      handler: async (args) => {
        totalToolCalls += 1;
        const { content } = args as { content?: string };
        const originalContent = content?.trim();
        if (!originalContent) {
          return { response: { result: 'ignored', reason: 'empty_content', board: [...boardContents] } };
        }

        const lastDiagramEntry = [...entriesMirror]
          .reverse()
          .find((entry) => entry.render === 'number_line'
            || entry.render === 'coordinate_plane'
            || entry.render === 'geometry');

        if (!lastDiagramEntry || !lastDiagramEntry.render) {
          return { response: { result: 'error', reason: 'no_diagram_found', board: [...boardContents] } };
        }

        let deltaPayload: BoardEntry['payload'] | undefined;
        try {
          if (lastDiagramEntry.render === 'number_line') {
            deltaPayload = await generateNumberLinePayload(originalContent, diagramContext);
          } else if (lastDiagramEntry.render === 'coordinate_plane') {
            deltaPayload = await generateCoordinatePlanePayload(originalContent, diagramContext);
          } else if (lastDiagramEntry.render === 'geometry') {
            deltaPayload = await generateGeometryPayload(originalContent, diagramContext);
          }
        } catch (error) {
          console.warn('[update_last_diagram] Diagram generation failed:', error);
          return {
            response: {
              result: 'error',
              reason: error instanceof Error ? error.message : 'diagram_generation_failed',
              board: [...boardContents],
            },
          };
        }

        if (!deltaPayload) {
          return { response: { result: 'error', reason: 'empty_payload', board: [...boardContents] } };
        }

        const mergedPayload = mergeDiagramPayload(lastDiagramEntry.render, lastDiagramEntry.payload, deltaPayload);
        const mergedContent = `${lastDiagramEntry.content}\n${originalContent}`.trim();
        const nextEntry: BoardEntry = {
          ...lastDiagramEntry,
          content: mergedContent,
          payload: mergedPayload,
          timestamp: Date.now(),
        };

        updateEntry?.(lastDiagramEntry.id, nextEntry);
        const mirrorIndex = entriesMirror.findIndex((entry) => entry.id === lastDiagramEntry.id);
        if (mirrorIndex !== -1) {
          entriesMirror[mirrorIndex] = nextEntry;
        }

        boardContents.push(originalContent);

        return {
          response: {
            result: 'ok',
            render: lastDiagramEntry.render,
            payload: mergedPayload,
            board: [...boardContents],
          },
        };
      },
    },
  ];

  return { tools, getBoardContents: () => [...boardContents] };
}



/**
 * Initial message sent to Gemini to start the problem-solving session.
 */
export const V2_INITIAL_MESSAGE =
  "Hi! I'm looking at the problem on the board.";

/**
 * Initial message sent to Gemini to start a history/social studies question session.
 * This nudges the tutor to lead proactively instead of waiting passively.
 */
export const V2_HISTORY_INITIAL_MESSAGE =
  "Please take the Lead Learner role: briefly analyze the history/social studies problem on the board, connect one key context clue, and ask me one guiding background/evidence question.";

/**
 * Initial message sent to Gemini to start the interactive concept session.
 */
export const V2_INTERACTIVE_CONCEPT_INITIAL_MESSAGE =
  "Hi.";
